/**
 * Client-side NID / CR80 card corner detection + automatic perspective crop.
 *
 * No OpenCV, no WASM, no network: this is a compact port of the classic
 * document-scanner pipeline, running on a downscaled copy of the photo so the
 * whole detection costs a few hundred milliseconds even for a 12 MP upload.
 *
 *   1. Downscale to `MAX_DETECT_DIM`, grayscale, Gaussian blur, Sobel, Canny
 *      (a strict edge map for segmentation, a permissive one for lines/scoring).
 *   2. Region path — build SEVERAL independent card masks, because no single
 *      binarisation survives every lighting/background combination:
 *        a. windowed edge-density — text/photo-rich card on a plain background
 *        b. Otsu on gray, both polarities — card brighter or darker than bg
 *        c. colour distance from the border-ring background colour
 *        d. regions enclosed by a closed Canny contour — uniform card, similar bg
 *        e. background flood from the frame border — separates a light card from
 *           light paper, where every global threshold fails
 *      Then: morphological close → connected components → convex hull →
 *      Douglas–Peucker epsilon sweep for a 4-point quad (min-area rect as a
 *      fallback) → per-edge total-least-squares line fit for sub-pixel corners.
 *   3. Line path — gradient-steered Hough transform, then every quad made of two
 *      near-parallel line pairs that cross at roughly 90°. This one keeps working
 *      when the card touches or overlaps other objects, which breaks any mask.
 *   4. Score all candidates from both paths. Measured against real phone photos
 *      of a desk, the cues that actually separate a card from background clutter
 *      are: CR80 aspect, CONTIGUOUS edge coverage per side (a card border is one
 *      unbroken line — four collinear fragments are not), a consistent brightness
 *      step across the whole outline (a card is lighter, or darker, than its
 *      surroundings all the way round), busy inside vs quiet just outside,
 *      printed-content density, and a plausible size. Quads with implausible
 *      corner angles or lopsided opposite sides are dropped outright.
 *
 * Corners come back in **natural image pixels**, ordered TL, TR, BR, BL — the
 * order `warpQuadToRect` / `isConvexQuad` expect — and normalised so the card's
 * long edge is horizontal. `null` is returned whenever the detector is unsure,
 * so callers can fall back to the manual crop.
 */

import { NID_CARD_ASPECT_RATIO, NID_CARD_CROP_OUTPUT_PX } from './nidCardConstants';
import { warpQuadToRect, isConvexQuad } from '../shared/perspectiveHomography';
import { classifyCardSide, MIN_SIDE_CONFIDENCE } from './classifyCardSide';

/** @typedef {[number, number]} Pt */

/** Long-edge working size for detection (corners are scaled back up at the end). */
const MAX_DETECT_DIM = 512;
/** Landscape width ÷ height of a CR80 card (~1.588). */
const CARD_AR = NID_CARD_ASPECT_RATIO;
/** Gaussian width of the aspect-ratio score — how much AR drift still scores well. */
const AR_SIGMA = 0.24;
/** Quads smaller than this share of the frame are never a card. */
const MIN_AREA_FRAC = 0.04;
/** At/above this share the "card" is just the whole photo. */
const FULL_FRAME_FRAC = 0.985;
/** Below this score the detector declines and the upload is left untouched. */
export const MIN_AUTO_CROP_CONFIDENCE = 0.55;
/**
 * Side confidence required to route an upload whose crop was NOT good enough to
 * keep. Measured on a bench of 100 synthetic phone photos: at this bar the
 * rejected-crop path calls 8 of 15 correctly for 1 mistake, where the old
 * behaviour called none of them.
 */
const FALLBACK_SIDE_CONFIDENCE = 0.5;
/**
 * Face evidence (see `classifyCardSide`) above which a candidate crop is treated
 * as an actual card face rather than as whatever else was lying on the table.
 */
const CARD_FACE_EVIDENCE = 0.5;
/** Hard aspect-ratio gate (long ÷ short side). */
const AR_MIN = 1.15;
const AR_MAX = 2.45;

/* ────────────────────────── small geometry helpers ────────────────────────── */

function polyArea(pts) {
    let a = 0;
    for (let i = 0; i < pts.length; i++) {
        const [x1, y1] = pts[i];
        const [x2, y2] = pts[(i + 1) % pts.length];
        a += x1 * y2 - x2 * y1;
    }
    return Math.abs(a) / 2;
}

const dist = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1]);

/** Andrew's monotone chain. Returns the hull in screen-clockwise order. */
function convexHull(points) {
    if (points.length < 4) return points.slice();
    const pts = points.slice().sort((p, q) => (p[0] - q[0]) || (p[1] - q[1]));
    const cross = (o, a, b) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
    const lower = [];
    for (const p of pts) {
        while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop();
        lower.push(p);
    }
    const upper = [];
    for (let i = pts.length - 1; i >= 0; i--) {
        const p = pts[i];
        while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop();
        upper.push(p);
    }
    lower.pop();
    upper.pop();
    return lower.concat(upper);
}

/** Douglas–Peucker over an OPEN chain. */
function simplifyChain(pts, eps) {
    const n = pts.length;
    if (n < 3) return pts.slice();
    const keep = new Uint8Array(n);
    keep[0] = 1;
    keep[n - 1] = 1;
    const stack = [[0, n - 1]];
    while (stack.length) {
        const [a, b] = stack.pop();
        if (b <= a + 1) continue;
        const [ax, ay] = pts[a];
        const dx = pts[b][0] - ax;
        const dy = pts[b][1] - ay;
        const len = Math.hypot(dx, dy) || 1;
        let best = -1;
        let bestD = -1;
        for (let i = a + 1; i < b; i++) {
            const d = Math.abs((pts[i][0] - ax) * dy - (pts[i][1] - ay) * dx) / len;
            if (d > bestD) { bestD = d; best = i; }
        }
        if (bestD > eps) {
            keep[best] = 1;
            stack.push([a, best], [best, b]);
        }
    }
    const out = [];
    for (let i = 0; i < n; i++) if (keep[i]) out.push(pts[i]);
    return out;
}

/** `cv.approxPolyDP(..., closed = true)` equivalent. */
function approxPolyClosed(poly, eps) {
    const n = poly.length;
    if (n <= 4) return poly.slice();
    let far = 0;
    let farD = -1;
    for (let i = 1; i < n; i++) {
        const d = dist(poly[0], poly[i]);
        if (d > farD) { farD = d; far = i; }
    }
    const a = simplifyChain(poly.slice(0, far + 1), eps);
    const b = simplifyChain(poly.slice(far).concat([poly[0]]), eps);
    return a.slice(0, -1).concat(b.slice(0, -1));
}

/** Min-area rotated rectangle over a hull (brute force per hull edge — hulls are small). */
function minAreaRect(hull) {
    let best = null;
    for (let i = 0; i < hull.length; i++) {
        const p = hull[i];
        const q = hull[(i + 1) % hull.length];
        let ux = q[0] - p[0];
        let uy = q[1] - p[1];
        const L = Math.hypot(ux, uy);
        if (L < 1e-6) continue;
        ux /= L;
        uy /= L;
        const vx = -uy;
        const vy = ux;
        let minU = Infinity;
        let maxU = -Infinity;
        let minV = Infinity;
        let maxV = -Infinity;
        for (const hp of hull) {
            const u = hp[0] * ux + hp[1] * uy;
            const v = hp[0] * vx + hp[1] * vy;
            if (u < minU) minU = u;
            if (u > maxU) maxU = u;
            if (v < minV) minV = v;
            if (v > maxV) maxV = v;
        }
        const area = (maxU - minU) * (maxV - minV);
        if (!best || area < best.area) best = { area, ux, uy, vx, vy, minU, maxU, minV, maxV };
    }
    if (!best) return null;
    const P = (u, v) => [best.ux * u + best.vx * v, best.uy * u + best.vy * v];
    return [
        P(best.minU, best.minV),
        P(best.maxU, best.minV),
        P(best.maxU, best.maxV),
        P(best.minU, best.maxV),
    ];
}

/** Order 4 points TL, TR, BR, BL (screen-clockwise, y down). */
function orderQuad(pts) {
    const cx = (pts[0][0] + pts[1][0] + pts[2][0] + pts[3][0]) / 4;
    const cy = (pts[0][1] + pts[1][1] + pts[2][1] + pts[3][1]) / 4;
    const cw = pts
        .slice()
        .sort((a, b) => Math.atan2(a[1] - cy, a[0] - cx) - Math.atan2(b[1] - cy, b[0] - cx));
    let start = 0;
    let bestSum = Infinity;
    for (let i = 0; i < 4; i++) {
        const s = cw[i][0] + cw[i][1];
        if (s < bestSum) { bestSum = s; start = i; }
    }
    return [cw[start], cw[(start + 1) % 4], cw[(start + 2) % 4], cw[(start + 3) % 4]];
}

/** Rotate the corner labels so the card's LONG edge becomes the top edge. */
function normaliseLandscape(quad) {
    const top = (dist(quad[0], quad[1]) + dist(quad[3], quad[2])) / 2;
    const side = (dist(quad[0], quad[3]) + dist(quad[1], quad[2])) / 2;
    if (side <= top * 1.02) return quad;
    return [quad[3], quad[0], quad[1], quad[2]];
}

/** Total-least-squares line through points → { a, b, c } with a·x + b·y + c = 0. */
function fitLine(points) {
    const n = points.length;
    let mx = 0;
    let my = 0;
    for (const p of points) { mx += p[0]; my += p[1]; }
    mx /= n;
    my /= n;
    let sxx = 0;
    let syy = 0;
    let sxy = 0;
    for (const p of points) {
        const dx = p[0] - mx;
        const dy = p[1] - my;
        sxx += dx * dx;
        syy += dy * dy;
        sxy += dx * dy;
    }
    const theta = 0.5 * Math.atan2(2 * sxy, sxx - syy);
    const dx = Math.cos(theta);
    const dy = Math.sin(theta);
    const a = -dy;
    const b = dx;
    return { a, b, c: -(a * mx + b * my) };
}

function lineIntersect(l1, l2) {
    const d = l1.a * l2.b - l2.a * l1.b;
    if (Math.abs(d) < 1e-9) return null;
    return [
        (l1.b * l2.c - l2.b * l1.c) / d,
        (l2.a * l1.c - l1.a * l2.c) / d,
    ];
}

/**
 * Snap the quad onto the real card edges: every hull point that sits near an
 * edge votes for that edge's line, then neighbouring lines are intersected.
 * A refined corner that runs away from the raw one is discarded.
 */
function refineQuad(quad, hull, tol) {
    const lines = [];
    for (let k = 0; k < 4; k++) {
        const p = quad[k];
        const q = quad[(k + 1) % 4];
        const ex = q[0] - p[0];
        const ey = q[1] - p[1];
        const len2 = ex * ex + ey * ey;
        if (len2 < 1e-6) return quad;
        const len = Math.sqrt(len2);
        const near = [];
        for (const h of hull) {
            const t = ((h[0] - p[0]) * ex + (h[1] - p[1]) * ey) / len2;
            if (t < 0.06 || t > 0.94) continue;
            const d = Math.abs((h[0] - p[0]) * ey - (h[1] - p[1]) * ex) / len;
            if (d <= tol) near.push(h);
        }
        lines.push(near.length >= 6 ? fitLine(near) : null);
    }
    const out = quad.slice();
    for (let k = 0; k < 4; k++) {
        const prev = lines[(k + 3) % 4];
        const cur = lines[k];
        if (!prev || !cur) continue;
        const p = lineIntersect(prev, cur);
        if (!p || !Number.isFinite(p[0]) || !Number.isFinite(p[1])) continue;
        if (dist(p, quad[k]) > tol * 3) continue;
        out[k] = p;
    }
    return out;
}

/**
 * Snap a quad onto the image's own edges.
 *
 * `refineQuad` can only fit the lines its MASK found, so when the mask swallowed
 * the card's drop shadow — which is what happens to a white card on a pale desk,
 * where the shadow is the only thing separating the two — the quad is a faithful
 * fit to the wrong outline. This pass forgets the mask: along each side it walks
 * a short way out and back, looking for the strongest brightness step running
 * parallel to that side, and re-fits the side to where the steps actually are.
 *
 * Two rules keep it from wandering onto the wrong line: a step only counts when
 * its gradient points across the side rather than along it, and of the offsets
 * that score well the nearest one wins, so a strong line further out (a table
 * edge, a sleeve) cannot pull the side off a weaker but real card border.
 *
 * @param {Pt[]} quad ordered TL TR BR BL
 * @param {Float32Array} gx @param {Float32Array} gy gradients from `sobel`
 * @param {number} search how far to look, in pixels, either side
 */
function snapQuadToEdges(quad, gx, gy, w, h, search) {
    const SAMPLES = 32;
    const lines = [];

    for (let k = 0; k < 4; k++) {
        const p = quad[k];
        const q = quad[(k + 1) % 4];
        const ex = q[0] - p[0];
        const ey = q[1] - p[1];
        const len = Math.hypot(ex, ey);
        if (len < 8) return quad;
        const ux = ex / len;
        const uy = ey / len;
        const nx = -uy;
        const ny = ux;

        /** Gradient across this side at one point, ignoring gradients along it. */
        const acrossAt = (x, y) => {
            const xi = Math.round(x);
            const yi = Math.round(y);
            if (xi < 1 || yi < 1 || xi >= w - 1 || yi >= h - 1) return 0;
            const i = yi * w + xi;
            const across = gx[i] * nx + gy[i] * ny;
            const along = gx[i] * ux + gy[i] * uy;
            // An edge running along this side has its gradient across it; a
            // diagonal one belongs to something else.
            return Math.abs(across) >= Math.abs(along) ? Math.abs(across) : 0;
        };

        // One profile for the WHOLE side rather than a vote per sample: a faint
        // border is below the noise at any single point but stands out clearly
        // once the side is summed along its length.
        const profile = new Float64Array(2 * search + 1);
        for (let s = 0; s < SAMPLES; s++) {
            const t = 0.08 + (0.84 * s) / (SAMPLES - 1);
            const bx = p[0] + ex * t;
            const by = p[1] + ey * t;
            for (let d = -search; d <= search; d++) {
                profile[d + search] += acrossAt(bx + nx * d, by + ny * d);
            }
        }

        let peak = 0;
        for (let d = 0; d < profile.length; d++) if (profile[d] > peak) peak = profile[d];
        if (peak <= 0) return quad;
        // Of the offsets that are nearly as good as the best, take the one
        // closest to where the quad already is: a stronger line further out (the
        // table's own edge, a sleeve) must not drag the side off a weaker but
        // real card border.
        let shift = 0;
        let bestScore = -1;
        for (let d = -search; d <= search; d++) {
            const v = profile[d + search];
            if (v < peak * 0.75) continue;
            if (bestScore < 0 || Math.abs(d) < Math.abs(shift)) {
                shift = d;
                bestScore = v;
            }
        }

        // Then let each sample find the exact crossing within a narrow band
        // around that shift, so a side that is slightly rotated is fixed too.
        const band = Math.max(1, Math.round(search * 0.4));
        const points = [];
        for (let s = 0; s < SAMPLES; s++) {
            const t = 0.08 + (0.84 * s) / (SAMPLES - 1);
            const bx = p[0] + ex * t;
            const by = p[1] + ey * t;
            let localBest = 0;
            let localOffset = null;
            for (let d = shift - band; d <= shift + band; d++) {
                const v = acrossAt(bx + nx * d, by + ny * d);
                if (v > localBest) {
                    localBest = v;
                    localOffset = d;
                }
            }
            if (localOffset === null) continue;
            points.push([bx + nx * localOffset, by + ny * localOffset]);
        }

        // Too little agreement to re-fit the side; keep the shift alone, which is
        // still better than leaving the side where the mask put it.
        if (points.length < SAMPLES * 0.35) {
            lines.push(fitLine([
                [p[0] + nx * shift, p[1] + ny * shift],
                [q[0] + nx * shift, q[1] + ny * shift],
            ]));
            continue;
        }
        lines.push(fitLine(points));
    }

    const out = quad.slice();
    for (let k = 0; k < 4; k++) {
        const corner = lineIntersect(lines[(k + 3) % 4], lines[k]);
        if (!corner || !Number.isFinite(corner[0]) || !Number.isFinite(corner[1])) return quad;
        // A corner that bolts means the sides were fitted to different objects;
        // keep the quad that at least came from one coherent outline.
        if (dist(corner, quad[k]) > search * 2.5) return quad;
        out[k] = corner;
    }
    const ordered = orderQuad(out);
    return isConvexQuad(ordered) ? ordered : quad;
}

/* ─────────────────────────── raster helpers ─────────────────────────── */

/** Separable binary dilate/erode with a square structuring element, O(w·h). */
function morph(src, w, h, r, dilateMode) {
    const tmp = new Uint8Array(w * h);
    const out = new Uint8Array(w * h);
    for (let y = 0; y < h; y++) {
        const row = y * w;
        let count = 0;
        for (let x = 0; x <= Math.min(r, w - 1); x++) count += src[row + x];
        for (let x = 0; x < w; x++) {
            const win = Math.min(w - 1, x + r) - Math.max(0, x - r) + 1;
            tmp[row + x] = dilateMode ? (count > 0 ? 1 : 0) : (count === win ? 1 : 0);
            const rem = x - r;
            const add = x + r + 1;
            if (rem >= 0) count -= src[row + rem];
            if (add < w) count += src[row + add];
        }
    }
    for (let x = 0; x < w; x++) {
        let count = 0;
        for (let y = 0; y <= Math.min(r, h - 1); y++) count += tmp[y * w + x];
        for (let y = 0; y < h; y++) {
            const win = Math.min(h - 1, y + r) - Math.max(0, y - r) + 1;
            out[y * w + x] = dilateMode ? (count > 0 ? 1 : 0) : (count === win ? 1 : 0);
            const rem = y - r;
            const add = y + r + 1;
            if (rem >= 0) count -= tmp[rem * w + x];
            if (add < h) count += tmp[add * w + x];
        }
    }
    return out;
}

const closeMask = (m, w, h, r) => morph(morph(m, w, h, r, true), w, h, r, false);

/** Otsu threshold over a 0..255 histogram. */
function otsuThreshold(hist, total) {
    let sum = 0;
    for (let t = 0; t < 256; t++) sum += t * hist[t];
    let sumB = 0;
    let wB = 0;
    let maxVar = -1;
    let threshold = 0;
    for (let t = 0; t < 256; t++) {
        wB += hist[t];
        if (wB === 0) continue;
        const wF = total - wB;
        if (wF === 0) break;
        sumB += t * hist[t];
        const mB = sumB / wB;
        const mF = (sum - sumB) / wF;
        const between = wB * wF * (mB - mF) * (mB - mF);
        if (between > maxVar) { maxVar = between; threshold = t; }
    }
    return threshold;
}

/** 5-tap separable Gaussian (σ ≈ 1.1). */
function blur(gray, w, h) {
    const K = [0.06136, 0.24477, 0.38774, 0.24477, 0.06136];
    const tmp = new Float32Array(w * h);
    const out = new Float32Array(w * h);
    for (let y = 0; y < h; y++) {
        const row = y * w;
        for (let x = 0; x < w; x++) {
            let s = 0;
            for (let k = -2; k <= 2; k++) {
                const xx = Math.min(w - 1, Math.max(0, x + k));
                s += gray[row + xx] * K[k + 2];
            }
            tmp[row + x] = s;
        }
    }
    for (let x = 0; x < w; x++) {
        for (let y = 0; y < h; y++) {
            let s = 0;
            for (let k = -2; k <= 2; k++) {
                const yy = Math.min(h - 1, Math.max(0, y + k));
                s += tmp[yy * w + x] * K[k + 2];
            }
            out[y * w + x] = s;
        }
    }
    return out;
}

/**
 * 3×3 box blur of the RGB planes. The flood mask compares neighbouring pixels
 * against a tolerance of a dozen levels, so sensor noise has to go first or a
 * faint card/paper step leaks.
 */
function blurRGB(data, w, h) {
    const tmp = new Uint8ClampedArray(data.length);
    const out = new Uint8ClampedArray(data.length);
    for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
            const i = (y * w + x) * 4;
            const x0 = x > 0 ? -1 : 0;
            const x1 = x < w - 1 ? 1 : 0;
            for (let c = 0; c < 3; c++) {
                let s = 0;
                let n = 0;
                for (let k = x0; k <= x1; k++) { s += data[i + k * 4 + c]; n++; }
                tmp[i + c] = s / n;
            }
            tmp[i + 3] = 255;
        }
    }
    for (let y = 0; y < h; y++) {
        const y0 = y > 0 ? -1 : 0;
        const y1 = y < h - 1 ? 1 : 0;
        for (let x = 0; x < w; x++) {
            const i = (y * w + x) * 4;
            for (let c = 0; c < 3; c++) {
                let s = 0;
                let n = 0;
                for (let k = y0; k <= y1; k++) { s += tmp[i + k * w * 4 + c]; n++; }
                out[i + c] = s / n;
            }
            out[i + 3] = 255;
        }
    }
    return out;
}

/** Sobel gradients + magnitude. */
function sobel(gray, w, h) {
    const gx = new Float32Array(w * h);
    const gy = new Float32Array(w * h);
    const mag = new Float32Array(w * h);
    let max = 0;
    for (let y = 1; y < h - 1; y++) {
        for (let x = 1; x < w - 1; x++) {
            const i = y * w + x;
            const a =
                gray[i - 1 - w] + 2 * gray[i - 1] + gray[i - 1 + w] -
                (gray[i + 1 - w] + 2 * gray[i + 1] + gray[i + 1 + w]);
            const b =
                gray[i - w - 1] + 2 * gray[i - w] + gray[i - w + 1] -
                (gray[i + w - 1] + 2 * gray[i + w] + gray[i + w + 1]);
            gx[i] = a;
            gy[i] = b;
            const m = Math.hypot(a, b);
            mag[i] = m;
            if (m > max) max = m;
        }
    }
    return { gx, gy, mag, max };
}

/** Otsu-derived upper Canny threshold for a gradient magnitude map. */
function cannyHighThreshold(mag, magMax, w, h) {
    const hist = new Uint32Array(256);
    const s = 255 / (magMax || 1);
    for (let i = 0; i < w * h; i++) hist[Math.min(255, (mag[i] * s) | 0)]++;
    return Math.max(12, otsuThreshold(hist, w * h)) / s;
}

/** Canny: non-maximum suppression + hysteresis. */
function canny(gx, gy, mag, w, h, hi, lo) {
    const nms = new Float32Array(w * h);
    for (let y = 1; y < h - 1; y++) {
        for (let x = 1; x < w - 1; x++) {
            const i = y * w + x;
            const m = mag[i];
            if (m < lo) continue;
            const ax = Math.abs(gx[i]);
            const ay = Math.abs(gy[i]);
            let n1;
            let n2;
            if (ax >= ay * 2.414) { n1 = mag[i - 1]; n2 = mag[i + 1]; }
            else if (ay >= ax * 2.414) { n1 = mag[i - w]; n2 = mag[i + w]; }
            else if ((gx[i] > 0) === (gy[i] > 0)) { n1 = mag[i - w - 1]; n2 = mag[i + w + 1]; }
            else { n1 = mag[i - w + 1]; n2 = mag[i + w - 1]; }
            if (m >= n1 && m >= n2) nms[i] = m;
        }
    }

    const edge = new Uint8Array(w * h);
    const stack = [];
    for (let i = 0; i < w * h; i++) {
        if (nms[i] >= hi) { edge[i] = 1; stack.push(i); }
    }
    while (stack.length) {
        const i = stack.pop();
        const x = i % w;
        const y = (i / w) | 0;
        for (let dy = -1; dy <= 1; dy++) {
            for (let dx = -1; dx <= 1; dx++) {
                const nx = x + dx;
                const ny = y + dy;
                if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
                const n = ny * w + nx;
                if (!edge[n] && nms[n] >= lo) { edge[n] = 1; stack.push(n); }
            }
        }
    }
    return edge;
}

/* ───────────────── Hough lines → quads (segmentation-free path) ───────────────── */

const DEG = Math.PI / 180;

/**
 * Gradient-steered Hough transform. Each edge pixel only votes for angles close
 * to its own gradient normal, which keeps the accumulator sharp and cheap.
 * @returns {{ a: number, b: number, rho: number, theta: number, votes: number }[]}
 */
function houghLines(edgeMap, gx, gy, w, h, maxLines) {
    const TH = 180;
    const D = Math.ceil(Math.hypot(w, h));
    const RH = 2 * D + 1;
    const acc = new Uint16Array(TH * RH);
    const cosT = new Float32Array(TH);
    const sinT = new Float32Array(TH);
    for (let t = 0; t < TH; t++) {
        const a = (t * Math.PI) / TH;
        cosT[t] = Math.cos(a);
        sinT[t] = Math.sin(a);
    }
    for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
            const i = y * w + x;
            if (!edgeMap[i]) continue;
            let phi = Math.atan2(gy[i], gx[i]);
            if (phi < 0) phi += Math.PI;
            const tc = Math.round((phi * TH) / Math.PI) % TH;
            for (let d = -4; d <= 4; d++) {
                const t = ((tc + d) % TH + TH) % TH;
                const rho = Math.round(x * cosT[t] + y * sinT[t]) + D;
                if (rho >= 0 && rho < RH) acc[t * RH + rho]++;
            }
        }
    }

    const minVotes = Math.max(20, Math.round(0.10 * Math.min(w, h)));
    const peaks = [];
    for (let t = 0; t < TH; t++) {
        for (let r = 0; r < RH; r++) {
            const v = acc[t * RH + r];
            if (v < minVotes) continue;
            let isMax = true;
            for (let dt = -2; dt <= 2 && isMax; dt++) {
                const tt = ((t + dt) % TH + TH) % TH;
                for (let dr = -6; dr <= 6; dr++) {
                    const rr = r + dr;
                    if (rr < 0 || rr >= RH) continue;
                    const u = acc[tt * RH + rr];
                    if (u > v || (u === v && tt * RH + rr < t * RH + r)) { isMax = false; break; }
                }
            }
            if (isMax) peaks.push({ t, rho: r - D, votes: v });
        }
    }
    peaks.sort((p, q) => q.votes - p.votes);
    return peaks.slice(0, maxLines).map((p) => ({
        a: cosT[p.t],
        b: sinT[p.t],
        rho: p.rho,
        theta: (p.t * Math.PI) / TH,
        votes: p.votes,
    }));
}

/** Union of two line sets, dropping near-duplicates (same angle and offset). */
function mergeLines(a, b) {
    const out = a.slice();
    for (const l of b) {
        const dup = out.some((o) => Math.abs(o.theta - l.theta) < 3 * DEG && Math.abs(o.rho - l.rho) < 4);
        if (!dup) out.push(l);
    }
    return out.sort((p, q) => q.votes - p.votes);
}

const angleGap = (a, b) => {
    const d = Math.abs(a - b) % Math.PI;
    return Math.min(d, Math.PI - d);
};

/** Intersection of two Hough lines (a·x + b·y = rho). */
function houghIntersect(l1, l2) {
    const det = l1.a * l2.b - l2.a * l1.b;
    if (Math.abs(det) < 1e-6) return null;
    return [
        (l1.rho * l2.b - l2.rho * l1.b) / det,
        (l1.a * l2.rho - l2.a * l1.rho) / det,
    ];
}

/**
 * Every card-shaped quad that four dominant lines can form: two near-parallel
 * pairs, roughly perpendicular to each other. Immune to the card touching or
 * overlapping other objects — unlike any region-growing mask.
 */
function houghQuads(lines, w, h) {
    const minSep = 0.12 * Math.min(w, h);
    const pairs = [];
    for (let i = 0; i < lines.length; i++) {
        for (let j = i + 1; j < lines.length; j++) {
            const li = lines[i];
            let lj = lines[j];
            // Strong perspective can splay two opposite card edges by ~30°.
            if (angleGap(li.theta, lj.theta) > 34 * DEG) continue;
            if (li.a * lj.a + li.b * lj.b < 0) lj = { ...lj, a: -lj.a, b: -lj.b, rho: -lj.rho };
            if (Math.abs(li.rho - lj.rho) < minSep) continue;
            pairs.push({ l1: li, l2: lj, theta: li.theta, votes: Math.min(li.votes, lj.votes) });
        }
    }
    pairs.sort((p, q) => q.votes - p.votes);
    const top = pairs.slice(0, 90);

    const margin = 0.18;
    const xLo = -w * margin;
    const xHi = w * (1 + margin);
    const yLo = -h * margin;
    const yHi = h * (1 + margin);

    const quads = [];
    for (let i = 0; i < top.length; i++) {
        for (let j = i + 1; j < top.length; j++) {
            const A = top[i];
            const B = top[j];
            const cross = angleGap(A.theta, B.theta);
            if (cross < 45 * DEG || cross > 135 * DEG) continue;
            const c1 = houghIntersect(A.l1, B.l1);
            const c2 = houghIntersect(A.l1, B.l2);
            const c3 = houghIntersect(A.l2, B.l2);
            const c4 = houghIntersect(A.l2, B.l1);
            if (!c1 || !c2 || !c3 || !c4) continue;
            const pts = [c1, c2, c3, c4];
            if (pts.some(([x, y]) => x < xLo || x > xHi || y < yLo || y > yHi)) continue;
            const quad = orderQuad(pts);
            if (!isConvexQuad(quad)) continue;
            // Cheap shape gate here keeps the expensive scoring pass small even
            // though many line pairs are tried.
            const tw2 = (dist(quad[0], quad[1]) + dist(quad[3], quad[2])) / 2;
            const sh2 = (dist(quad[0], quad[3]) + dist(quad[1], quad[2])) / 2;
            const ar = Math.max(tw2, sh2) / Math.max(1e-6, Math.min(tw2, sh2));
            if (ar < AR_MIN || ar > AR_MAX) continue;
            if (polyArea(quad) < w * h * MIN_AREA_FRAC) continue;
            quads.push(quad);
        }
    }
    return quads;
}

/** Connected components (4-connectivity). */
function labelComponents(mask, w, h) {
    const labels = new Int32Array(w * h);
    const comps = [];
    const stack = [];
    let cur = 0;
    for (let s = 0; s < w * h; s++) {
        if (!mask[s] || labels[s]) continue;
        cur++;
        let size = 0;
        let minx = w;
        let miny = h;
        let maxx = 0;
        let maxy = 0;
        stack.push(s);
        labels[s] = cur;
        while (stack.length) {
            const q = stack.pop();
            size++;
            const qx = q % w;
            const qy = (q / w) | 0;
            if (qx < minx) minx = qx;
            if (qx > maxx) maxx = qx;
            if (qy < miny) miny = qy;
            if (qy > maxy) maxy = qy;
            if (qx > 0) { const n = q - 1; if (mask[n] && !labels[n]) { labels[n] = cur; stack.push(n); } }
            if (qx < w - 1) { const n = q + 1; if (mask[n] && !labels[n]) { labels[n] = cur; stack.push(n); } }
            if (qy > 0) { const n = q - w; if (mask[n] && !labels[n]) { labels[n] = cur; stack.push(n); } }
            if (qy < h - 1) { const n = q + w; if (mask[n] && !labels[n]) { labels[n] = cur; stack.push(n); } }
        }
        comps.push({ label: cur, size, minx, miny, maxx, maxy });
    }
    return { labels, comps };
}

/** Border pixels of one component (input for the convex hull). */
function componentBoundary(labels, comp, w, h) {
    const pts = [];
    for (let y = comp.miny; y <= comp.maxy; y++) {
        for (let x = comp.minx; x <= comp.maxx; x++) {
            const i = y * w + x;
            if (labels[i] !== comp.label) continue;
            if (
                x === 0 || y === 0 || x === w - 1 || y === h - 1 ||
                labels[i - 1] !== comp.label || labels[i + 1] !== comp.label ||
                labels[i - w] !== comp.label || labels[i + w] !== comp.label
            ) {
                pts.push([x, y]);
            }
        }
    }
    return pts;
}

/* ─────────────────────────────── masks ─────────────────────────────── */

/** Windowed edge density — a printed card is dense with text/photo edges. */
function maskEdgeDensity(edge, w, h) {
    const II = new Float64Array((w + 1) * (h + 1));
    for (let y = 1; y <= h; y++) {
        let rowSum = 0;
        const off = y * (w + 1);
        const offUp = (y - 1) * (w + 1);
        const erow = (y - 1) * w;
        for (let x = 1; x <= w; x++) {
            rowSum += edge[erow + (x - 1)];
            II[off + x] = II[offUp + x] + rowSum;
        }
    }
    const K = Math.max(6, Math.round(Math.min(w, h) / 14));
    const DENS = 0.045;
    const mask = new Uint8Array(w * h);
    for (let y = 0; y < h; y++) {
        const y0 = Math.max(0, y - K);
        const y1 = Math.min(h, y + K + 1);
        for (let x = 0; x < w; x++) {
            const x0 = Math.max(0, x - K);
            const x1 = Math.min(w, x + K + 1);
            const s =
                II[y1 * (w + 1) + x1] - II[y0 * (w + 1) + x1] -
                II[y1 * (w + 1) + x0] + II[y0 * (w + 1) + x0];
            if (s / ((x1 - x0) * (y1 - y0)) > DENS) mask[y * w + x] = 1;
        }
    }
    return mask;
}

/** Otsu on gray; `invert` picks the darker side as foreground. */
/**
 * The printed area, found by local contrast rather than by edges.
 *
 * A white card on white paper or pale marble has no border worth the name: every
 * threshold puts card and table on the same side, and blur plus JPEG leave the
 * Canny map too thin for `maskEdgeDensity` to close a blob over it. What the
 * table never has is PRINT. Local standard deviation lights up wherever ink
 * varies against paper and stays near zero over any smooth surface, however
 * close its tone is to the card's — so this is the mask that finds the card
 * nobody else can see.
 *
 * The threshold is relative to the busiest part of this photo, because the
 * absolute contrast of a card in shade is a fraction of one in sunlight.
 */
function maskLocalVariance(gray, w, h) {
    const wide = w + 1;
    const sum = new Float64Array(wide * (h + 1));
    const sumSq = new Float64Array(wide * (h + 1));
    for (let y = 1; y <= h; y++) {
        let rs = 0;
        let rsq = 0;
        const off = y * wide;
        const up = (y - 1) * wide;
        const row = (y - 1) * w;
        for (let x = 1; x <= w; x++) {
            const v = gray[row + (x - 1)];
            rs += v;
            rsq += v * v;
            sum[off + x] = sum[up + x] + rs;
            sumSq[off + x] = sumSq[up + x] + rsq;
        }
    }

    const K = Math.max(4, Math.round(Math.min(w, h) / 26));
    const std = new Float64Array(w * h);
    let peak = 0;
    for (let y = 0; y < h; y++) {
        const y0 = Math.max(0, y - K);
        const y1 = Math.min(h, y + K + 1);
        for (let x = 0; x < w; x++) {
            const x0 = Math.max(0, x - K);
            const x1 = Math.min(w, x + K + 1);
            const n = (x1 - x0) * (y1 - y0);
            const box = (t) => t[y1 * wide + x1] - t[y0 * wide + x1] - t[y1 * wide + x0] + t[y0 * wide + x0];
            const mean = box(sum) / n;
            const variance = Math.max(0, box(sumSq) / n - mean * mean);
            const v = Math.sqrt(variance);
            std[y * w + x] = v;
            if (v > peak) peak = v;
        }
    }
    if (peak < 4) return null; // a photo with no print anywhere in it

    const floor = Math.max(3, peak * 0.22);
    const mask = new Uint8Array(w * h);
    for (let i = 0; i < w * h; i++) mask[i] = std[i] > floor ? 1 : 0;
    return mask;
}

function maskOtsu(gray, w, h, invert) {
    const hist = new Uint32Array(256);
    for (let i = 0; i < w * h; i++) hist[Math.min(255, Math.max(0, gray[i] | 0))]++;
    const t = otsuThreshold(hist, w * h);
    const mask = new Uint8Array(w * h);
    for (let i = 0; i < w * h; i++) {
        const on = invert ? gray[i] < t : gray[i] >= t;
        if (on) mask[i] = 1;
    }
    return mask;
}

/** Colour distance from the median colour of the outer border ring. */
function maskBackgroundColour(data, w, h) {
    const bw = Math.max(2, Math.round(Math.min(w, h) * 0.05));
    const rs = [];
    const gs = [];
    const bs = [];
    for (let y = 0; y < h; y++) {
        const edgeRow = y < bw || y >= h - bw;
        for (let x = 0; x < w; x++) {
            if (!edgeRow && x >= bw && x < w - bw) continue;
            const p = (y * w + x) * 4;
            rs.push(data[p]);
            gs.push(data[p + 1]);
            bs.push(data[p + 2]);
        }
    }
    const median = (arr) => {
        if (!arr.length) return 0;
        arr.sort((a, b) => a - b);
        return arr[arr.length >> 1];
    };
    const bgR = median(rs);
    const bgG = median(gs);
    const bgB = median(bs);

    const distMap = new Float32Array(w * h);
    let maxD = 0;
    for (let i = 0, p = 0; i < w * h; i++, p += 4) {
        const dr = data[p] - bgR;
        const dg = data[p + 1] - bgG;
        const db = data[p + 2] - bgB;
        const d = Math.sqrt(dr * dr + dg * dg + db * db);
        distMap[i] = d;
        if (d > maxD) maxD = d;
    }
    if (maxD < 24) return null;
    const hist = new Uint32Array(256);
    const s = 255 / maxD;
    for (let i = 0; i < w * h; i++) hist[Math.min(255, (distMap[i] * s) | 0)]++;
    const t = Math.max(20, otsuThreshold(hist, w * h)) / s;
    const mask = new Uint8Array(w * h);
    for (let i = 0; i < w * h; i++) if (distMap[i] > t) mask[i] = 1;
    return mask;
}

/**
 * "Magic wand from the frame border": grow the BACKGROUND inwards from the image
 * edge, stopping at Canny edges and at colour steps. Far more sensitive than a
 * global threshold — it separates a light card from light paper (a step of a few
 * levels is enough) while still tolerating vignetting and gradient lighting.
 * Everything the flood cannot reach is card.
 *
 * @param {Uint8ClampedArray} data RGBA
 * @param {Uint8Array} wall dilated edge map (hard barrier)
 * @param {number} localTol max colour step between neighbouring pixels
 * @param {number} globalTol max colour drift from the border-ring colour
 */
function maskBackgroundFlood(data, wall, w, h, localTol, globalTol) {
    const n = w * h;
    const bg = new Uint8Array(n);
    const queue = new Int32Array(n);
    let qh = 0;
    let qt = 0;

    // Seed colour = median of the outer ring (robust to a card touching one side).
    const ring = Math.max(1, Math.round(Math.min(w, h) * 0.02));
    const rs = [];
    const gs = [];
    const bs = [];
    for (let y = 0; y < h; y++) {
        const edgeRow = y < ring || y >= h - ring;
        for (let x = 0; x < w; x++) {
            if (!edgeRow && x >= ring && x < w - ring) continue;
            const p = (y * w + x) * 4;
            rs.push(data[p]);
            gs.push(data[p + 1]);
            bs.push(data[p + 2]);
        }
    }
    const med = (a) => { a.sort((p, q) => p - q); return a[a.length >> 1]; };
    const sr = med(rs);
    const sg = med(gs);
    const sb = med(bs);

    const near = (i, r, g, b, tol) => {
        const p = i * 4;
        const dr = data[p] - r;
        const dg = data[p + 1] - g;
        const db = data[p + 2] - b;
        return dr * dr + dg * dg + db * db <= tol * tol;
    };
    const seed = (i) => {
        if (bg[i] || wall[i]) return;
        if (!near(i, sr, sg, sb, globalTol)) return;
        bg[i] = 1;
        queue[qt++] = i;
    };
    for (let x = 0; x < w; x++) { seed(x); seed((h - 1) * w + x); }
    for (let y = 0; y < h; y++) { seed(y * w); seed(y * w + w - 1); }

    while (qh < qt) {
        const i = queue[qh++];
        const x = i % w;
        const y = (i / w) | 0;
        const p = i * 4;
        const cr = data[p];
        const cg = data[p + 1];
        const cb = data[p + 2];
        const visit = (j) => {
            if (bg[j] || wall[j]) return;
            if (!near(j, cr, cg, cb, localTol)) return;
            if (!near(j, sr, sg, sb, globalTol)) return;
            bg[j] = 1;
            queue[qt++] = j;
        };
        if (x > 0) visit(i - 1);
        if (x < w - 1) visit(i + 1);
        if (y > 0) visit(i - w);
        if (y < h - 1) visit(i + w);
    }

    let bgCount = 0;
    for (let i = 0; i < n; i++) bgCount += bg[i];
    // No usable background (card bleeds off every side) or nothing held it back.
    if (bgCount < n * 0.04 || bgCount > n * 0.97) return null;

    const mask = new Uint8Array(n);
    for (let i = 0; i < n; i++) mask[i] = bg[i] ? 0 : 1;
    return mask;
}

/**
 * Everything the Canny outline encloses. The card border is usually a closed
 * loop even when card and background share a colour, so a flood fill that
 * starts on the image border cannot reach the card interior.
 */
function maskEnclosedByEdges(wall, w, h) {
    const outside = new Uint8Array(w * h);
    const stack = [];
    const push = (i) => {
        if (!wall[i] && !outside[i]) { outside[i] = 1; stack.push(i); }
    };
    for (let x = 0; x < w; x++) { push(x); push((h - 1) * w + x); }
    for (let y = 0; y < h; y++) { push(y * w); push(y * w + w - 1); }
    while (stack.length) {
        const i = stack.pop();
        const x = i % w;
        const y = (i / w) | 0;
        if (x > 0) push(i - 1);
        if (x < w - 1) push(i + 1);
        if (y > 0) push(i - w);
        if (y < h - 1) push(i + w);
    }
    const mask = new Uint8Array(w * h);
    let inside = 0;
    for (let i = 0; i < w * h; i++) {
        if (!outside[i]) { mask[i] = 1; inside++; }
    }
    return inside > w * h * 0.03 ? mask : null;
}

/* ───────────────────────────── scoring ───────────────────────────── */

/**
 * How well the quad outline sits on real gradient edges.
 *
 * `frac` is the plain covered share, but on a cluttered photo any line hits
 * something often enough to look supported, so `run` — the LONGEST UNBROKEN
 * covered stretch of the weakest side — is what separates a card border (one
 * continuous line) from four unrelated fragments stitched into a big quad.
 */
function outlineSupport(quad, edgeDil, w, h) {
    let hit = 0;
    let total = 0;
    let worstRun = 1;
    for (let k = 0; k < 4; k++) {
        const p = quad[k];
        const q = quad[(k + 1) % 4];
        const steps = Math.max(8, Math.round(dist(p, q)));
        let run = 0;
        let best = 0;
        let gap = 0;
        for (let s = 0; s <= steps; s++) {
            const t = s / steps;
            const x = Math.round(p[0] + (q[0] - p[0]) * t);
            const y = Math.round(p[1] + (q[1] - p[1]) * t);
            const on = x >= 0 && y >= 0 && x < w && y < h && edgeDil[y * w + x];
            if (x >= 0 && y >= 0 && x < w && y < h) {
                total++;
                if (on) hit++;
            }
            if (on) {
                gap = 0;
                run++;
                if (run > best) best = run;
            } else if (++gap > 2) {       // tolerate pinhole gaps, not real breaks
                run = 0;
            } else {
                run++;
            }
        }
        const cover = best / (steps + 1);
        if (cover < worstRun) worstRun = cover;
    }
    return { frac: total ? hit / total : 0, run: worstRun };
}

/** Convex point-in-quad (quad is ordered, y down). */
function inQuad(quad, x, y) {
    let sign = 0;
    for (let i = 0; i < 4; i++) {
        const [x1, y1] = quad[i];
        const [x2, y2] = quad[(i + 1) % 4];
        const z = (x2 - x1) * (y - y1) - (y2 - y1) * (x - x1);
        if (z === 0) continue;
        const s = z > 0 ? 1 : -1;
        if (sign === 0) sign = s;
        else if (s !== sign) return false;
    }
    return true;
}

const scaleQuad = (quad, f) => {
    const cx = (quad[0][0] + quad[1][0] + quad[2][0] + quad[3][0]) / 4;
    const cy = (quad[0][1] + quad[1][1] + quad[2][1] + quad[3][1]) / 4;
    return quad.map(([x, y]) => [cx + (x - cx) * f, cy + (y - cy) * f]);
};

/**
 * A card's content stops at its border: dense inside, quiet in the ring just
 * outside. A quad slicing through the card scores ~0.5 here, the true outline
 * close to 1 — which is what separates them when both sit on strong edges.
 */
function contentContrast(quad, edgeMap, w, h) {
    const outer = scaleQuad(quad, 1.22);
    const [bx0, by0, bx1, by1] = bboxOf(outer);
    const step = Math.max(1, Math.round(Math.max(bx1 - bx0, by1 - by0) / 48));
    let inN = 0;
    let inHit = 0;
    let ringN = 0;
    let ringHit = 0;
    for (let y = Math.round(by0); y <= by1; y += step) {
        for (let x = Math.round(bx0); x <= bx1; x += step) {
            if (x < 0 || y < 0 || x >= w || y >= h) continue;
            const inner = inQuad(quad, x, y);
            if (!inner && !inQuad(outer, x, y)) continue;
            const on = edgeMap[y * w + x] ? 1 : 0;
            if (inner) { inN++; inHit += on; } else { ringN++; ringHit += on; }
        }
    }
    if (!inN) return { calm: 0.5, density: 0 };
    const dIn = inHit / inN;
    // A quad the size of the photo has its whole surround off-canvas, which would
    // otherwise read as "perfectly quiet outside" and score like a real card.
    const expectedRing = inN * (1.22 * 1.22 - 1);
    if (ringN < expectedRing * 0.35) return { calm: 0.4, density: dIn };
    const dRing = ringN ? ringHit / ringN : 0;
    return { calm: (dIn + 1e-4) / (dIn + dRing + 2e-4), density: dIn };
}

/**
 * A card is one object: it is either lighter than everything around it or
 * darker, so the brightness step across its outline keeps the SAME sign the
 * whole way round. A quad stitched together from unrelated object edges flips
 * sign side to side, which no amount of edge support can hide.
 */
function borderPolarity(quad, gray, w, h) {
    const cx = (quad[0][0] + quad[1][0] + quad[2][0] + quad[3][0]) / 4;
    const cy = (quad[0][1] + quad[1][1] + quad[2][1] + quad[3][1]) / 4;
    const off = Math.max(2, Math.round(Math.min(w, h) * 0.012));
    const at = (x, y) => gray[Math.round(y) * w + Math.round(x)];
    let pos = 0;
    let neg = 0;
    let total = 0;
    for (let k = 0; k < 4; k++) {
        const p = quad[k];
        const q = quad[(k + 1) % 4];
        const steps = 40;
        for (let s = 1; s < steps; s++) {
            const t = s / steps;
            const mx = p[0] + (q[0] - p[0]) * t;
            const my = p[1] + (q[1] - p[1]) * t;
            let nx = mx - cx;
            let ny = my - cy;
            const len = Math.hypot(nx, ny);
            if (len < 1e-6) continue;
            nx /= len;
            ny /= len;
            const ix = mx - nx * off;
            const iy = my - ny * off;
            const ox = mx + nx * off;
            const oy = my + ny * off;
            if (ix < 0 || iy < 0 || ix >= w || iy >= h) continue;
            if (ox < 0 || oy < 0 || ox >= w || oy >= h) continue;
            total++;
            const d = at(ix, iy) - at(ox, oy);
            if (d > 6) pos++;
            else if (d < -6) neg++;
        }
    }
    return total ? Math.max(pos, neg) / total : 0.5;
}

function scoreQuad(quad, fill, edgeDil, edgeMap, gray, w, h) {
    const quadArea = polyArea(quad);
    const frame = w * h;
    const areaFrac = quadArea / frame;
    // Too small to be the subject, or just the photo's own rectangle.
    if (areaFrac < MIN_AREA_FRAC || areaFrac >= FULL_FRAME_FRAC) return null;

    const top = (dist(quad[0], quad[1]) + dist(quad[3], quad[2])) / 2;
    const side = (dist(quad[0], quad[3]) + dist(quad[1], quad[2])) / 2;
    const minSide = Math.min(
        dist(quad[0], quad[1]), dist(quad[1], quad[2]),
        dist(quad[2], quad[3]), dist(quad[3], quad[0]),
    );
    if (minSide < Math.min(w, h) * 0.08) return null;
    const ar = Math.max(top, side) / Math.max(1e-6, Math.min(top, side));
    if (ar < AR_MIN || ar > AR_MAX) return null;

    // Opposite sides of a photographed rectangle stay within ~1.6× of each other
    // even at a steep angle. A quad with a 680 px side facing a 1660 px one is a
    // stitched-together shape whose lopsidedness the mean aspect ratio hides.
    const topLen = dist(quad[0], quad[1]);
    const botLen = dist(quad[3], quad[2]);
    const leftLen = dist(quad[0], quad[3]);
    const rightLen = dist(quad[1], quad[2]);
    const oppRatio = (a, b) => Math.max(a, b) / Math.max(1e-6, Math.min(a, b));
    if (oppRatio(topLen, botLen) > 1.9 || oppRatio(leftLen, rightLen) > 1.9) return null;

    // A photographed rectangle stays roughly right-angled: even a steep phone
    // angle keeps every corner inside ~70–110°. Anything sharper is a random
    // trapezoid stitched out of unrelated edges.
    for (let k = 0; k < 4; k++) {
        const p = quad[(k + 3) % 4];
        const c = quad[k];
        const q = quad[(k + 1) % 4];
        const v1x = p[0] - c[0];
        const v1y = p[1] - c[1];
        const v2x = q[0] - c[0];
        const v2y = q[1] - c[1];
        const n1 = Math.hypot(v1x, v1y);
        const n2 = Math.hypot(v2x, v2y);
        if (n1 < 1e-6 || n2 < 1e-6) return null;
        const cosA = (v1x * v2x + v1y * v2y) / (n1 * n2);
        if (Math.abs(cosA) > 0.53) return null; // outside ~58°–122°
    }

    const arScore = Math.exp(-((ar - CARD_AR) ** 2) / (2 * AR_SIGMA * AR_SIGMA));
    const { frac: support, run } = outlineSupport(quad, edgeDil, w, h);
    const { calm, density } = contentContrast(quad, edgeMap, w, h);
    const polarity = borderPolarity(quad, gray, w, h);
    // An ID card is printed: photo, logos, several lines of text. A blank
    // rectangle (a book, a tile, a coloured box) has almost no interior edges.
    const content = Math.min(1, density / 0.05);
    // Held or laid on a desk, a card covers roughly a tenth to two thirds of the
    // shot. Rewarding "bigger is better" instead lets a frame-sized quad stitched
    // out of background clutter beat the real card.
    const areaScore = Math.exp(-((Math.log(areaFrac / 0.28) ** 2) / (2 * 0.95 * 0.95)));

    return {
        score:
            0.22 * arScore + 0.10 * fill + 0.08 * support + 0.16 * run +
            0.14 * polarity + 0.12 * calm + 0.11 * content + 0.07 * areaScore,
        areaFrac,
        ar,
        support,
        run,
        content,
        calm,
    };
}

const bboxOf = (quad) => {
    let x0 = Infinity;
    let y0 = Infinity;
    let x1 = -Infinity;
    let y1 = -Infinity;
    for (const [x, y] of quad) {
        if (x < x0) x0 = x;
        if (x > x1) x1 = x;
        if (y < y0) y0 = y;
        if (y > y1) y1 = y;
    }
    return [x0, y0, x1, y1];
};

function bboxIoU(a, b) {
    const ix = Math.max(0, Math.min(a[2], b[2]) - Math.max(a[0], b[0]));
    const iy = Math.max(0, Math.min(a[3], b[3]) - Math.max(a[1], b[1]));
    const inter = ix * iy;
    const areaA = Math.max(0, a[2] - a[0]) * Math.max(0, a[3] - a[1]);
    const areaB = Math.max(0, b[2] - b[0]) * Math.max(0, b[3] - b[1]);
    const uni = areaA + areaB - inter;
    return uni > 0 ? inter / uni : 0;
}

/* ─────────────────────────── main detection ─────────────────────────── */

/** Hull → best 4-point quad (perspective-preserving DP first, min-area rect fallback). */
function quadFromHull(hull) {
    let perim = 0;
    for (let i = 0; i < hull.length; i++) perim += dist(hull[i], hull[(i + 1) % hull.length]);
    if (perim <= 0) return null;
    for (const f of [0.010, 0.015, 0.02, 0.03, 0.04, 0.055, 0.07, 0.09]) {
        const approx = approxPolyClosed(hull, f * perim);
        if (approx.length === 4) {
            const q = orderQuad(approx);
            if (isConvexQuad(q)) return q;
        }
    }
    const rect = minAreaRect(hull);
    if (!rect) return null;
    const q = orderQuad(rect);
    return isConvexQuad(q) ? q : null;
}

/**
 * Detect every card-shaped quadrilateral in an already-decoded image.
 * @param {HTMLImageElement|HTMLCanvasElement} img
 * @param {{ maxCards?: number }} [opts]
 * @returns {{ quad: Pt[], confidence: number, areaFrac: number }[]}
 */
export function detectCardsFromImage(img, { maxCards = 3 } = {}) {
    if (typeof document === 'undefined') return [];
    const NW = img.naturalWidth || img.width;
    const NH = img.naturalHeight || img.height;
    if (!NW || !NH) return [];

    const scale = Math.min(1, MAX_DETECT_DIM / Math.max(NW, NH));
    const w = Math.max(1, Math.round(NW * scale));
    const h = Math.max(1, Math.round(NH * scale));

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return [];
    ctx.drawImage(img, 0, 0, w, h);
    let data;
    try {
        ({ data } = ctx.getImageData(0, 0, w, h));
    } catch {
        return []; // tainted canvas (cross-origin source)
    }

    const gray = new Float32Array(w * h);
    for (let i = 0, p = 0; i < w * h; i++, p += 4) {
        gray[i] = 0.299 * data[p] + 0.587 * data[p + 1] + 0.114 * data[p + 2];
    }
    const smooth = blur(gray, w, h);
    const { gx, gy, mag, max } = sobel(smooth, w, h);
    if (max <= 0) return [];
    // Two edge maps: a strict one drives segmentation, a permissive one keeps the
    // faint border of a light card on light paper alive for lines and scoring.
    const hi = cannyHighThreshold(mag, max, w, h);
    const edge = canny(gx, gy, mag, w, h, hi, hi * 0.4);
    const edgeSoft = canny(gx, gy, mag, w, h, hi * 0.45, hi * 0.18);
    const edgeDil = morph(edgeSoft, w, h, 2, true);

    const wall = morph(edge, w, h, 1, true);
    const rgb = blurRGB(data, w, h);
    const dense = maskEdgeDensity(edge, w, h);
    const masks = [
        dense,
        maskLocalVariance(smooth, w, h),
        maskOtsu(smooth, w, h, false),
        maskOtsu(smooth, w, h, true),
        maskBackgroundColour(data, w, h),
        maskEnclosedByEdges(wall, w, h),
        maskBackgroundFlood(data, wall, w, h, 12, 46),
        maskBackgroundFlood(data, wall, w, h, 26, 90),
        // Same flood on de-noised colour: catches a card whose tone barely
        // differs from the paper, where per-pixel noise breaks the strict pass.
        maskBackgroundFlood(rgb, wall, w, h, 10, 40),
    ].filter(Boolean);

    const closeR = Math.max(2, Math.round(Math.min(w, h) / 90));
    const minCompSize = w * h * 0.03;
    const refineTol = Math.max(3, Math.round(Math.min(w, h) * 0.012));
    // How far the gradient snap may move a side. Wide enough to cross a drop
    // shadow, narrow enough that it cannot reach the next object along.
    const snapSearch = Math.max(3, Math.round(Math.min(w, h) * 0.04));

    /** @type {{quad: Pt[], score: number, areaFrac: number, bbox: number[]}[]} */
    const candidates = [];
    for (const raw of masks) {
        const mask = closeMask(raw, w, h, closeR);
        const { labels, comps } = labelComponents(mask, w, h);
        const useful = comps
            .filter((c) => c.size >= minCompSize &&
                (c.maxx - c.minx + 1) >= w * 0.12 &&
                (c.maxy - c.miny + 1) >= h * 0.1)
            .sort((a, b) => b.size - a.size)
            .slice(0, 4);
        for (const comp of useful) {
            const boundary = componentBoundary(labels, comp, w, h);
            if (boundary.length < 12) continue;
            const hull = convexHull(boundary);
            if (hull.length < 4) continue;
            let quad = quadFromHull(hull);
            if (!quad) continue;
            quad = refineQuad(quad, hull, refineTol);
            quad = orderQuad(quad);
            if (!isConvexQuad(quad)) continue;
            quad = snapQuadToEdges(quad, gx, gy, w, h, snapSearch);
            const quadArea = polyArea(quad);
            const hullArea = polyArea(hull);
            const fill = quadArea > 0 && hullArea > 0
                ? Math.min(hullArea, quadArea) / Math.max(hullArea, quadArea)
                : 0;
            const s = scoreQuad(quad, fill, edgeDil, edgeSoft, smooth, w, h);
            if (!s) continue;
            candidates.push({ quad, score: s.score, areaFrac: s.areaFrac, bbox: bboxOf(quad) });
        }
    }

    // Segmentation-free path: four dominant straight lines forming a card.
    // Edges deep inside the printed area (text rows, the photo box) are dropped
    // first — otherwise they own every Hough peak and a faint card border, which
    // is the line we actually want, never makes the shortlist.
    // Two line sets, because neither alone covers both worlds: the plain map wins
    // on a busy desk (where "dense" covers the whole frame and suppression would
    // delete the card outline), the suppressed one wins on a plain background
    // (where text rows would otherwise own every peak).
    const interior = morph(dense, w, h, Math.max(3, Math.round(Math.min(w, h) / 22)), false);
    const borderEdges = new Uint8Array(w * h);
    for (let i = 0; i < w * h; i++) borderEdges[i] = edgeSoft[i] && !interior[i] ? 1 : 0;
    // A real photo of a desk holds dozens of strong lines (keyboard, monitor,
    // table edges); a shortlist of ~16 never reaches the card. The contiguity
    // gate below is what keeps the wider net from letting rubbish through.
    const lines = mergeLines(
        houghLines(edgeSoft, gx, gy, w, h, 40),
        houghLines(borderEdges, gx, gy, w, h, 24),
    );
    for (const raw of houghQuads(lines, w, h)) {
        const quad = snapQuadToEdges(raw, gx, gy, w, h, snapSearch);
        const s = scoreQuad(quad, 1, edgeDil, edgeSoft, smooth, w, h);
        // A line quad is only real if its sides are backed by a continuous edge
        // rather than by four collinear fragments that happen to line up across a
        // busy background — UNLESS the quad is full of printed matter with a
        // quiet margin outside it, which clutter is not. A card photographed on a
        // surface close to its own tone has a real but broken border, and the
        // strict contiguity gate was throwing exactly those away; the face
        // re-rank in detectCardFaceFromImage is what now keeps the wider net
        // honest.
        const printed = s && s.content >= 0.9 && s.calm >= 0.5 && s.run >= 0.15;
        if (!s || s.support < 0.55 || (s.run < 0.4 && !printed)) continue;
        candidates.push({ quad, score: s.score, areaFrac: s.areaFrac, bbox: bboxOf(quad) });
    }

    if (!candidates.length) return [];

    candidates.sort((a, b) => b.score - a.score);
    /** @type {typeof candidates} */
    const kept = [];
    for (const c of candidates) {
        if (kept.some((k) => bboxIoU(k.bbox, c.bbox) > 0.35)) continue;
        kept.push(c);
        if (kept.length >= maxCards) break;
    }

    const inv = 1 / scale;
    return kept.map((c) => ({
        quad: normaliseLandscape(c.quad).map(([x, y]) => [
            Math.max(0, Math.min(NW, x * inv)),
            Math.max(0, Math.min(NH, y * inv)),
        ]),
        confidence: Math.max(0, Math.min(1, c.score)),
        areaFrac: c.areaFrac,
    }));
}

/**
 * Best card quadrilateral for an image, in natural pixels, ordered TL, TR, BR, BL.
 * @param {HTMLImageElement|HTMLCanvasElement} img
 * @returns {{ quad: Pt[], confidence: number, areaFrac: number, cardCount: number } | null}
 */
export function detectCardQuadFromImage(img) {
    const cards = detectCardsFromImage(img);
    if (!cards.length) return null;
    return { ...cards[0], cardCount: cards.length };
}

/* ───────────────────────── auto-crop entry point ───────────────────────── */

/** Load a File / Blob / object-URL into a decoded HTMLImageElement. */
function loadImageElement(fileOrUrl) {
    return new Promise((resolve, reject) => {
        const isFile = typeof fileOrUrl !== 'string';
        const url = isFile ? URL.createObjectURL(fileOrUrl) : fileOrUrl;
        const img = new Image();
        img.onload = () => {
            if (isFile) URL.revokeObjectURL(url);
            resolve(img);
        };
        img.onerror = (e) => {
            if (isFile) URL.revokeObjectURL(url);
            reject(e);
        };
        img.src = url;
    });
}

/** Side call for an image we are NOT rectifying (already-cropped or undetected). */
function readSide(source) {
    const cls = classifyCardSide(source);
    return {
        side: cls && cls.confidence >= MIN_SIDE_CONFIDENCE ? cls.side : null,
        sideConfidence: cls?.confidence ?? 0,
    };
}

function croppedName(name) {
    if (!name) return 'nid-cropped.jpg';
    const dot = name.lastIndexOf('.');
    const base = dot > 0 ? name.slice(0, dot) : name;
    return `${base}-cropped.jpg`;
}

/**
 * Warp one detected quad to a flat CR80 canvas at `NID_CARD_CROP_OUTPUT_PX`.
 * Warping runs at a higher interim resolution, then downsamples — same path the
 * manual crop panel uses, so auto and manual crops are pixel-comparable.
 * @param {HTMLImageElement} img
 * @param {Pt[]} quad natural-pixel corners, TL TR BR BL
 * @param {boolean} [flip180] draw the card rotated half a turn (upside-down photo)
 * @returns {HTMLCanvasElement}
 */
export function warpQuadToCardCanvas(img, quad, flip180 = false) {
    const { w: tw, h: th } = NID_CARD_CROP_OUTPUT_PX;
    const interimW = Math.min(2048, Math.max(tw * 2, img.naturalWidth || img.width));
    const interimH = Math.round(interimW / CARD_AR);
    const warped = warpQuadToRect(img, quad, interimW, interimH);
    const out = document.createElement('canvas');
    out.width = tw;
    out.height = th;
    const ctx = out.getContext('2d');
    if (!ctx) throw new Error('canvas');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, tw, th);
    if (flip180) {
        ctx.translate(tw, th);
        ctx.rotate(Math.PI);
    }
    ctx.drawImage(warped, 0, 0, tw, th);
    return out;
}

/** Cheap warp used only to look at a candidate's content, not to keep. */
function warpPreviewCanvas(img, quad, width = 360) {
    const height = Math.round(width / CARD_AR);
    const warped = warpQuadToRect(img, quad, width, height);
    return warped;
}

/**
 * The card face in a photo: the geometric shortlist, re-ranked by whether each
 * candidate crop actually looks like an NID.
 *
 * Geometry alone cannot tell the card from the notebook, phone or envelope lying
 * beside it — all of them are bright quadrilaterals with clean edges, and on a
 * busy desk one of them routinely outscores the card. Content alone cannot pick
 * a crop either, since a loose quad around the card still shows the card. So the
 * two are combined: a candidate wins on its border evidence AND on carrying a
 * portrait, an emblem or a barcode where a card face carries them.
 *
 * @param {HTMLImageElement|HTMLCanvasElement} img
 * @returns {{ quad: Pt[], confidence: number, areaFrac: number, cardCount: number,
 *             side: 'front'|'back'|null, sideConfidence: number, flip180: boolean } | null}
 */
export function detectCardFaceFromImage(img, { maxCards = 4 } = {}) {
    const cards = detectCardsFromImage(img, { maxCards });
    if (!cards.length) return null;

    let best = null;
    for (const card of cards) {
        let cls = null;
        try {
            cls = classifyCardSide(warpPreviewCanvas(img, card.quad));
        } catch {
            cls = null;
        }
        const evidence = Math.max(0, Math.min(1, (cls?.evidence ?? 0) / 1.2));
        // Looking like a card face comes FIRST, and geometry only ranks within
        // that. A notebook or a phone case scores well on borders and badly on
        // face evidence, and no amount of clean geometry should let it win over
        // the quad that actually holds a portrait, an emblem or a barcode.
        const looksLikeCard = evidence >= CARD_FACE_EVIDENCE ? 1 : 0;
        const rank = looksLikeCard * 2 + card.confidence + 0.55 * evidence;
        if (!best || rank > best.rank) {
            best = {
                rank,
                card,
                side: cls && cls.confidence >= MIN_SIDE_CONFIDENCE ? cls.side : null,
                sideConfidence: cls?.confidence ?? 0,
                flip180: !!cls?.flip180,
            };
        }
    }

    return {
        ...best.card,
        cardCount: cards.length,
        side: best.side,
        sideConfidence: best.sideConfidence,
        flip180: best.flip180,
    };
}

function canvasToJpegBlob(canvas, quality = 0.92) {
    return new Promise((resolve, reject) => {
        canvas.toBlob(
            (blob) => (blob ? resolve(blob) : reject(new Error('blob'))),
            'image/jpeg',
            quality,
        );
    });
}

/** Warp one detected quad straight to a CR80 JPEG blob. */
export async function warpQuadToCardBlob(img, quad, flip180 = false) {
    return canvasToJpegBlob(warpQuadToCardCanvas(img, quad, flip180));
}

/**
 * Detect the card, straighten it, and say which side it is.
 * The ORIGINAL file comes back untouched whenever detection is not confident,
 * so an upload is never made worse — the manual crop stays one click away.
 *
 * @param {File} file already-decodable image (convert HEIC before calling)
 * @param {{ minConfidence?: number }} [opts]
 * @returns {Promise<{ file: File, detected: boolean, confidence: number,
 *                    side: 'front'|'back'|null, sideConfidence: number }>}
 */
export async function autoCropCardFile(file, { minConfidence = MIN_AUTO_CROP_CONFIDENCE } = {}) {
    const fail = { file, detected: false, confidence: 0, side: null, sideConfidence: 0 };
    if (typeof document === 'undefined' || !file) return fail;
    let img;
    try {
        img = await loadImageElement(file);
    } catch {
        return fail;
    }
    try {
        const det = detectCardFaceFromImage(img);
        if (!det || det.confidence < minConfidence) {
            // The crop is not good enough to replace the user's file with — but
            // the quad is often still roughly right, and a side call read off it
            // is worth having, since it only decides which slot the untouched
            // upload goes in. It has to clear a higher bar than a clean crop's.
            const unsure = det?.side && det.sideConfidence >= FALLBACK_SIDE_CONFIDENCE ? det.side : null;
            return {
                file,
                detected: false,
                confidence: det?.confidence ?? 0,
                side: unsure,
                sideConfidence: unsure ? det.sideConfidence : 0,
            };
        }
        // Already a tight card scan (quad ≈ the whole frame, frame ≈ CR80): leave it
        // alone rather than risk shaving a pixel row off a good crop.
        const NW = img.naturalWidth || img.width;
        const NH = img.naturalHeight || img.height;
        const frameAr = NW / Math.max(1, NH);
        if (det.areaFrac >= 0.97 && Math.abs(frameAr - CARD_AR) / CARD_AR < 0.12) {
            return { file, detected: true, confidence: det.confidence, ...readSide(img) };
        }
        // Classify on the rectified card, where every landmark has a fixed home —
        // and let it undo a half-turn when the card was photographed upside down.
        let canvas = warpQuadToCardCanvas(img, det.quad);
        const cls = classifyCardSide(canvas);
        if (cls?.flip180) canvas = warpQuadToCardCanvas(img, det.quad, true);
        const blob = await canvasToJpegBlob(canvas);
        return {
            file: new File([blob], croppedName(file.name), { type: 'image/jpeg' }),
            detected: true,
            confidence: det.confidence,
            side: cls && cls.confidence >= MIN_SIDE_CONFIDENCE ? cls.side : null,
            sideConfidence: cls?.confidence ?? 0,
        };
    } catch {
        return fail;
    }
}
